import { Navigate } from "react-router-dom";

import { useAuthSession } from "@/lib/session-context";
import { isTabAuthorized, type TabRoute } from "@/lib/routes";

interface TabGuardProps {
  tab: TabRoute;
  children: React.ReactNode;
}

/**
 * Guards a detail-page sub-tab against direct-URL access when the caller lacks
 * its `requiredPermission` / `requiredFeature`. The tab bar already hides
 * ungranted tabs (the parent detail page filters via `isTabAuthorized`), but a
 * deep link like `/deals/:id/rfqs` would otherwise render the tab element and
 * only fail on the API call — this bounces it to the parent instead.
 *
 * The parent detail route redirects a bare `/deals/:id` to its first *visible*
 * tab, so navigating up lands somewhere the caller can actually see. Server-side
 * `/api/*` checks remain the source of truth — this is UX.
 */
export function TabGuard({ tab, children }: TabGuardProps) {
  const session = useAuthSession();

  if (
    !isTabAuthorized(
      tab,
      session.features,
      session.permissions,
      session.is_supplier,
    )
  ) {
    // Bounce up one level (strip the trailing `/tab` segment). The parent's
    // index redirect then sends the caller to their first visible tab.
    return <Navigate to=".." replace relative="path" />;
  }

  return <>{children}</>;
}
